import React, { useState } from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Divider, Flex, Collapse, Tag
} from "@chakra-ui/react";
import {
  FaTint, FaChevronDown, FaChevronUp, FaShieldAlt,
  FaExclamationTriangle, FaFlask, FaInfoCircle
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

interface AccordionItemProps {
  heading: string;
  tag: string;
  tagColor: string;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ heading, tag, tagColor, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
      <Box
        as="button"
        w="full"
        textAlign="left"
        p={5}
        onClick={() => setOpen(!open)}
        bg={open ? ICE_MIST : "white"}
        _hover={{ bg: ICE_MIST }}
        transition="background 0.2s"
      >
        <Flex justify="space-between" align="center" gap={4}>
          <HStack spacing={3} flex={1} flexWrap="wrap">
            <Text
              fontSize="xs"
              fontWeight="700"
              px={2}
              py={0.5}
              borderRadius="full"
              bg={tagColor + "20"}
              color={tagColor}
              whiteSpace="nowrap"
            >
              {tag}
            </Text>
            <Text fontWeight="600" color={NAVY} fontSize="sm">{heading}</Text>
          </HStack>
          <Icon
            as={open ? FaChevronUp : FaChevronDown}
            color={DEEP_AQUA}
            flexShrink={0}
            boxSize={3}
          />
        </Flex>
      </Box>
      <Collapse in={open}>
        <Box p={5} pt={0} bg="white" borderTop="1px solid" borderColor="gray.100">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const WaterBlogChlorine: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/chlorine-in-tap-water"
        fallbackTitle="Chlorine & Chloramine in Tap Water: What the Science Actually Shows — One Water Australia"
        fallbackDescription="Most Australians don't realise their tap water now contains chloramine, not just chlorine. What this means for your health, your plumbing, and your filtration choices."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Health & Science
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Chlorine & Chloramine in Tap Water
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              Australian tap water is disinfected for good reason. But the conversation doesn't end at the treatment plant — and most Australians don't know what disinfectant is actually in their water.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Tag bg={WATER_BLUE + "30"} color={WATER_BLUE} fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                Investigative
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                ~12 min read
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                16 citations
              </Tag>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=80"
          alt="Water quality laboratory testing — chlorine and disinfection byproducts"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* The achievement — acknowledge first */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Start Here: The Public Health Achievement</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={6}>
              <Text color="gray.700" fontSize="lg" lineHeight="tall" fontWeight="500">
                Chlorination of drinking water is one of the most consequential public health advances in human history. Cholera, typhoid, and dysentery — diseases that killed tens of thousands of Australians annually in the early 20th century — are essentially eliminated because of it.
              </Text>
            </Box>
            <Text color="gray.700" mb={4} lineHeight="tall">
              This is the correct starting point for any honest discussion. Nobody seriously advocates removing disinfectants from water distribution. The question this article addresses is more specific: what happens to those disinfectants between the treatment plant and your glass, and what do the byproducts mean for long-term health?
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              It's also worth stating clearly: if you're asking whether your tap water is acutely dangerous today, the answer for most Australians is no. The risks being discussed here are chronic — the result of low-level exposure accumulated over years and decades. That's exactly why they're worth understanding rather than dismissing.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What's actually in your water */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>What's Actually in Your Water: Chlorine or Chloramine?</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`} mb={6}>
              <HStack mb={2}>
                <Icon as={FaInfoCircle} color={DEEP_AQUA} />
                <Text fontWeight="700" color="gray.800" fontSize="sm">Most Queenslanders have chloramine in their tap water — not free chlorine</Text>
              </HStack>
              <Text fontSize="sm" color="gray.600" lineHeight="tall">
                Seqwater switched from free chlorine to monochloramine as the primary distribution disinfectant in the early 2000s. The reason was compliance: chloramine produces fewer regulated trihalomethanes (THMs) and haloacetic acids (HAAs) than free chlorine. Most residents were never informed of this switch.
              </Text>
            </Box>
            <Text color="gray.700" mb={4} lineHeight="tall">
              The distinction matters because chlorine and chloramine behave differently, produce different byproducts, require different filtration, and interact with infrastructure in different ways. Much of what people know about "chlorine" in tap water applies only partially — or not at all — to chloramine.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack mb={3}>
                  <Icon as={FaTint} color={DEEP_AQUA} />
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Free Chlorine (Cl₂)</Text>
                </HStack>
                <VStack align="stretch" spacing={2}>
                  {[
                    "Stronger, faster-acting disinfectant",
                    "Dissipates in open water (leaves standing)",
                    "Removed efficiently by activated carbon",
                    "Reacts with NOM to form THMs and HAAs",
                    "More volatile — higher inhalation risk in showers",
                    "Standard pool/spa disinfectant",
                  ].map((p) => (
                    <Text key={p} fontSize="xs" color="gray.600">• {p}</Text>
                  ))}
                </VStack>
              </Box>
              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack mb={3}>
                  <Icon as={FaFlask} color={DEEP_AQUA} />
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Chloramine (NH₂Cl)</Text>
                </HStack>
                <VStack align="stretch" spacing={2}>
                  {[
                    "Weaker disinfectant, more persistent in pipes",
                    "Does NOT dissipate from standing water",
                    "NOT removed by standard activated carbon",
                    "Forms different DBPs: NDMA, iodoacids, chlorophenols",
                    "Corrodes rubber fittings and certain plastics",
                    "Cannot be used in dialysis or aquariums",
                  ].map((p) => (
                    <Text key={p} fontSize="xs" color="gray.600">• {p}</Text>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
            <Text color="gray.500" lineHeight="tall" fontSize="sm" fontStyle="italic">
              Note: The shift to chloramine solved one regulatory problem (THM/HAA limits under ADWG) while creating a different set of chemical concerns and infrastructure interactions that were not fully anticipated. This is not a hypothetical: the consequences played out in documented events, including a major lead contamination crisis in Washington, DC.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* DBPs — the science */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Disinfection Byproducts: What the Research Actually Shows</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Disinfection byproducts (DBPs) are chemicals formed when disinfectants react with naturally occurring organic matter in water. Over 700 DBPs have been identified in disinfected drinking water. The ones monitored under Australia's guidelines represent a fraction of this total.
            </Text>
            <VStack spacing={3} align="stretch" mb={6}>
              <AccordionItem heading="Trihalomethanes (THMs): Regulated, but Contested" tag="Regulated" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    THMs — including chloroform (CHCl₃), bromodichloromethane, and dibromochloromethane — form when free chlorine reacts with natural organic matter (NOM). They are the most studied class of DBPs.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The ADWG guideline value</Text> for total THMs is 0.25 mg/L. Australian utilities typically report compliance. However, utilities report averages across distribution zones — not at individual taps. If your suburb receives water at the end of a distribution loop, or your home has a long dead-end line, your actual THM levels may be higher than the zone average.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Cancer associations:</Text> Epidemiological studies consistently associate long-term high-end THM exposure with increased bladder cancer risk. A landmark meta-analysis by Villanueva et al. (2004, American Journal of Epidemiology) found a pooled odds ratio of 1.4 for bladder cancer with lifetime residential consumption of chlorinated drinking water. The International Agency for Research on Cancer (IARC) classifies chloroform as a Group 2B possible human carcinogen.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Reproductive outcomes:</Text> Several epidemiological studies link elevated THM exposure during pregnancy to adverse birth outcomes including low birth weight and preterm birth. The evidence is not conclusive, but it has informed decisions by some European regulators to tighten limits.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    Key citations: Villanueva et al. (2004); Richardson et al. (2007); WHO IARC Monograph 73 (chloroform); Toledano et al. (2005) — trihalomethanes and stillbirth, very preterm and full-term birth
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Haloacetic Acids (HAAs): Under-monitored, Under-discussed" tag="Regulated" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    HAAs are the second major class of regulated DBPs. Unlike THMs, they don't volatilise — meaning they are not inhaled in showers and are primarily ingested. They are less frequently discussed in public health communications but carry their own concern profile.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Cancer associations:</Text> Dichloroacetic acid (DCA), one of the most common HAAs, is classified by IARC as Group 2A — probably carcinogenic to humans — based on sufficient animal evidence and limited human data. The ADWG guideline for DCA is 0.1 mg/L.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Monitoring gap:</Text> Many Australian water utilities report THMs routinely but do not report HAAs publicly in the same detail. The ADWG requires utilities to investigate if HAA levels may be of health significance, but reporting frameworks are less standardised than for THMs.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    Key citations: IARC Monograph 106 (dichloroacetic acid); Richardson, S.D. et al. (2007) — Occurrence, genotoxicity, and carcinogenicity of regulated and emerging DBPs; NHMRC ADWG (2022)
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Chloramine-Specific DBPs: NDMA and Iodoacids" tag="Unregulated in Australia" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    This is where the chloramine story becomes more complicated — and where most public communication stops short.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">N-Nitrosodimethylamine (NDMA):</Text> Chloramine reacts with certain secondary amines (present in some water sources) to form NDMA, a potent animal carcinogen classified as Group 2A (probably carcinogenic to humans) by IARC. NDMA is also the contaminant at the centre of major pharmaceutical recalls (ranitidine/Zantac, valsartan). It has no regulated guideline value in the current ADWG for drinking water, though the ADWG Technical Reports note it as an emerging concern.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Iodoacetic acids:</Text> When source water contains iodide (as some Australian surface waters do), chloramine can produce iodoacetic acids — a class of DBPs that some toxicological studies suggest may be more genotoxic than chlorinated or brominated equivalents. The human health significance is still being established, but they are formed specifically because of the switch to chloramine.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The regulatory gap:</Text> The ADWG has guideline values for the regulated THMs and HAAs. It does not have established guideline values for NDMA, most iodoacids, or the broader universe of chloramine-specific DBPs. Utilities switching to chloramine to reduce THMs and HAAs may be trading monitored DBPs for unmonitored ones — and we don't have good population data on the net health effect.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    Key citations: Schreiber & Mitch (2006) — Nitrosamine formation pathway; Krasner et al. (2006) — Occurrence of a new generation of DBPs; IARC Group 2A (NDMA); Richardson et al. (2010) — Integrated disinfection DBP mixtures research
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Shower Inhalation: The Exposure Route Most Studies Ignore" tag="Often overlooked" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Most DBP risk assessments focus on ingestion (drinking water). But volatile DBPs — particularly chloroform and other THMs — are released from hot water and inhaled during showers and baths. Dermal absorption also occurs through the skin.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">The quantitative finding:</Text> Research published by Jo et al. (1990) and Weisel & Jo (1996) found that for volatile DBPs, a 10-15 minute shower in chlorinated water can produce inhalation exposure equivalent to drinking approximately 2 litres of tap water. If you're calculating your DBP exposure purely from what you drink, you may be significantly underestimating total exposure.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Chloramine note:</Text> Because chloramine is less volatile than free chlorine, this inhalation pathway is somewhat reduced for chloramine-using utilities. However, the chloramine molecule itself and its breakdown products are still inhaled in smaller amounts, and the respiratory and skin contact effects of chloramine are not well characterised in long-term studies.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Asthma:</Text> Swimming pool studies (Kogevinas et al., 2010) have linked chlorinated water aerosol exposure to asthma development and exacerbation. Whether shower chlorine exposure in domestic settings produces similar effects is less studied but biologically plausible.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    Key citations: Jo, W.K. et al. (1990); Weisel & Jo (1996) — dermal/inhalation chloroform; Kogevinas et al. (2010) — occupational exposure in swimming pools and asthma
                  </Text>
                </VStack>
              </AccordionItem>
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The chloramine → lead story */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>The Chloramine Story Most Australians Haven't Heard</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={DEEP_AQUA} mb={6}>
              <HStack mb={2}>
                <Icon as={FaExclamationTriangle} color={NAVY} />
                <Text fontWeight="700" color="gray.800" fontSize="sm">Switching to chloramine to reduce one risk created a documented second risk</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                When Washington DC Water switched from free chlorine to chloramine in 2000 to comply with THM regulations, lead levels in household tap water increased dramatically — in some samples to over 100 times the US action level. The mechanism: chloramine does not form the same passivating oxide layer on lead and lead-soldered copper pipe as free chlorine does. It actively destabilised existing scale, releasing lead into the water. Children in DC were exposed to elevated lead for years before the problem was identified.
              </Text>
            </Box>
            <Text color="gray.700" mb={4} lineHeight="tall">
              This is not a hypothetical. Marc Edwards at Virginia Tech documented the event comprehensively. The DC Water chloramine switch is the event that informed his later work on Flint, Michigan. It established clearly that the choice of disinfectant has downstream consequences for metallic infrastructure that aren't captured in standard DBP monitoring.
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Australia's situation is different — lead service lines (the primary culprits in DC) are less common here than in the US, and lead was banned from solder in 1989 — but the underlying chemistry applies wherever lead-containing solder or older brass fittings exist. Homes built before the late 1980s, or with fixtures manufactured before Australian lead-free standards took effect, are potentially affected.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {[
                {
                  title: "Rubber degradation",
                  detail: "Chloramine accelerates degradation of certain rubber compounds, including older EPDM seals and gaskets. This can cause degraded particles to enter water and accelerate fitting failure.",
                  icon: FaExclamationTriangle,
                  color: "orange",
                },
                {
                  title: "Dialysis exclusion",
                  detail: "Both chlorine and chloramine must be removed from water used in renal dialysis. That chloramine is explicitly excluded from medical water use is a meaningful data point about its biological effects at higher exposure.",
                  icon: FaInfoCircle,
                  color: "blue",
                },
                {
                  title: "Aquarium & fish mortality",
                  detail: "Standard dechlorination tablets designed for free chlorine do not neutralise chloramine. Aquarium hobbyists across Queensland have reported fish deaths after utilities switched disinfectant. A canary in the coal mine.",
                  icon: FaTint,
                  color: "teal",
                },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Icon as={item.icon} color={DEEP_AQUA} mb={3} />
                  <Text fontWeight="700" color={NAVY} fontSize="sm" mb={2}>{item.title}</Text>
                  <Text fontSize="xs" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Community evidence */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Community Evidence and Reported Experiences</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Formal epidemiological studies are the gold standard, but they are designed to detect population-level signals — not to capture individual variation, infrastructure-specific exposures, or the pattern of community complaints that often precede formal recognition of a problem. The following are categories of reported experience that deserve acknowledgment alongside the peer-reviewed literature.
            </Text>
            <VStack spacing={4} align="stretch">
              {[
                {
                  title: "Smell and taste complaints after disinfectant changes",
                  detail: "Community water quality forums and council feedback channels regularly document complaint spikes when utilities switch disinfectants or alter chloramine dosing. The smell profile of chloramine (described variously as 'swimming pool', 'ammonia', or 'band-aid') is chemically distinct from free chlorine and is perceived differently. These aren't simply sensitive noses — chloramine does have a distinct and often stronger sensory signature at similar residual concentrations.",
                },
                {
                  title: "Skin and hair reports in chloramine-supplied areas",
                  detail: "Online communities of QLD residents report chloramine-linked skin dryness, scalp irritation, and hair texture changes. Because these reports are anecdotal and confounded by many variables (soap, humidity, diet), they don't constitute evidence. But chloramine is known to denature proteins and to be more difficult to remove by showering alone. The mechanism for skin effects is plausible, and the volume of consistent reports warrants acknowledgment.",
                },
                {
                  title: "Filtration mismatch: people filtering but not removing chloramine",
                  detail: "Hundreds of Australians have purchased standard activated carbon under-sink filters believing they are removing 'chlorine' from their water. They are not meaningfully removing chloramine — which requires catalytic carbon (GAC) or specific block carbon formulations. The gap between what consumers believe they are filtering and what they are actually filtering is significant, and not currently addressed in mainstream product labelling or utility communications.",
                },
                {
                  title: "Post-infrastructure complaint patterns",
                  detail: "Documented in Williamtown and other sites: communities near groundwater contamination events describe tap water quality changes that preceded formal recognition by regulators. The pattern of community observation → dismissal → eventual confirmation has now occurred enough times that community reports should be taken more seriously as early indicators, even where they haven't been validated by formal testing.",
                },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} fontSize="sm" mb={2}>{item.title}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What the monitoring framework captures */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What the Regulatory Framework Monitors — and What It Doesn't</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
                <HStack mb={3}>
                  <Icon as={FaShieldAlt} color={DEEP_AQUA} />
                  <Text fontWeight="700" color="gray.800" fontSize="sm">Monitored</Text>
                </HStack>
                <VStack align="stretch" spacing={1}>
                  {[
                    "Total THMs (guideline 0.25 mg/L)",
                    "Individual THM species (chloroform, BDCM, etc.)",
                    "5 HAA species (DCA, TCA, MCAA, DCAA, TCAA)",
                    "Chlorine/chloramine residual (0.5–2 mg/L typical)",
                    "Microbial indicators (E. coli, coliforms)",
                  ].map((s) => (
                    <Text key={s} fontSize="xs" color="gray.700">✓ {s}</Text>
                  ))}
                </VStack>
              </Box>
              <Box p={5} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <HStack mb={3}>
                  <Icon as={FaExclamationTriangle} color={DEEP_AQUA} />
                  <Text fontWeight="700" color="gray.800" fontSize="sm">Not Routinely Monitored</Text>
                </HStack>
                <VStack align="stretch" spacing={1}>
                  {[
                    "NDMA (no ADWG guideline value)",
                    "Iodoacetic acids (chloramine-specific)",
                    "Haloacetonitriles and chloropicrin",
                    "DBP exposure at the tap (vs. treatment works)",
                    "Seasonal variation in source water NOM load",
                    "Interaction effects between multiple DBPs",
                  ].map((s) => (
                    <Text key={s} fontSize="xs" color="gray.700">✗ {s}</Text>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              Australian water utilities report compliance with ADWG guideline values. The ADWG itself notes that it represents guidelines, not mandatory standards — enforcement mechanisms vary between states, and guideline values are typically assessed against distribution zone averages, not individual household samples.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              The gap between distribution point monitoring and tap-level exposure is most significant in properties with older plumbing, long dead-end distribution lines, or appliances that store or heat water (hot water systems, ice makers). Residence time in pipes increases DBP concentration; elevated temperature affects their speciation.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What you can do */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>Your Options — Matched to Your Actual Situation</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={6}>
              <Text color="gray.700" fontStyle="italic" lineHeight="tall">
                The first step is finding out what disinfectant your utility actually uses. If you're in SE QLD (Seqwater supply), it's chloramine. This changes which filtration technology is effective. Standard pitcher filters and many entry-level under-sink carbon filters are designed for free chlorine and will not adequately remove chloramine.
              </Text>
            </Box>
            <VStack spacing={4} align="stretch">
              {[
                {
                  option: "Do nothing",
                  detail: "A reasonable position if you're healthy, live in a property with newer plumbing, and aren't in a high-risk group (pregnant, immunocompromised, children). Australian tap water meets its regulatory requirements. The risks discussed here are chronic and probabilistic, not acute. But make this choice with full information, not because you assumed the risks were zero.",
                  level: "No action",
                  levelColor: "gray",
                },
                {
                  option: "Catalytic carbon under-sink filter",
                  detail: "For chloramine removal, you need catalytic granular activated carbon (GAC) or a catalytic carbon block filter — not standard activated carbon. Catalytic carbon has a modified surface chemistry that breaks down the chloramine bond. Confirm with your filter manufacturer that their product is rated for chloramine removal before purchasing. Addresses drinking and cooking water exposure.",
                  level: "Targeted — drinking water",
                  levelColor: DEEP_AQUA,
                },
                {
                  option: "Vitamin C or KDF shower filter",
                  detail: "Ascorbic acid (Vitamin C) neutralises both chlorine and chloramine on contact — the only reliable dechlorinator for chloramine in shower applications. KDF (copper-zinc alloy) media also removes chloramine. Standard carbon shower filters are not effective for chloramine. Addresses inhalation and dermal exposure routes.",
                  level: "Targeted — shower",
                  levelColor: DEEP_AQUA,
                },
                {
                  option: "Whole-home catalytic carbon filtration",
                  detail: "Point-of-entry treatment with catalytic carbon media removes chloramine from every tap, shower, and appliance. The most comprehensive approach for chloramine-supplied homes. Also eliminates rubber degradation effects on internal fittings. Typically requires professional sizing and installation to ensure adequate contact time.",
                  level: "Comprehensive",
                  levelColor: NAVY,
                },
                {
                  option: "Reverse osmosis (under-sink)",
                  detail: "RO removes essentially all DBPs from drinking water. Typically includes pre-carbon stages that handle chloramine before it reaches the RO membrane (which chloramine can damage). The highest-specification option for drinking water quality. Does not address shower or whole-home exposure.",
                  level: "High-specification drinking",
                  levelColor: NAVY,
                },
              ].map((item) => (
                <Box key={item.option} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Flex justify="space-between" align="flex-start" mb={3} flexWrap="wrap" gap={2}>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{item.option}</Text>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color={typeof item.levelColor === "string" && item.levelColor.startsWith("#") ? item.levelColor : `${item.levelColor}.600`}
                      bg={typeof item.levelColor === "string" && item.levelColor.startsWith("#") ? item.levelColor + "15" : `${item.levelColor}.50`}
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {item.level}
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Closing statement */}
          <Box p={8} bg={ICE_MIST} borderRadius="2xl" border={`1px solid ${WATER_BLUE}30`}>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color={NAVY}>Our Position</Heading>
              <Text color="gray.700" fontStyle="italic" lineHeight="tall" fontSize="lg">
                "We support the use of disinfectants in water distribution — without them, the public health gains of the past century would rapidly erode. What we don't support is the assumption that the job is finished once water leaves the treatment plant, or that the regulatory framework for DBPs is comprehensive enough to render individual action unnecessary. The switch from chlorine to chloramine in Australian utilities resolved some monitored risks and introduced others that are currently unmonitored. Informed households deserve to know which disinfectant is in their supply, what it forms as it travels through pipes, and what filtration actually works."
              </Text>
              <Text fontSize="xs" color="gray.500" fontStyle="normal">— One Water Australia</Text>
            </VStack>
          </Box>

          {/* Sources */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Sources & References</Heading>
            <VStack align="flex-start" spacing={1}>
              {[
                "Australian Drinking Water Guidelines (ADWG), NHMRC & NRMMC 2011 (updated 2022)",
                "World Health Organization — Guidelines for Drinking-water Quality, 4th Edition",
                "Seqwater Annual Drinking Water Quality Report 2025–26",
                "Villanueva, C.M. et al. — Bladder Cancer and Exposure to Water Disinfection By-Products Through Ingestion, Showering, and Swimming in Pools. Am J Epidemiol. 2007",
                "Richardson, S.D. et al. — Occurrence, Genotoxicity, and Carcinogenicity of Regulated and Emerging Disinfection By-Products in Drinking Water. Mutat Res. 2007",
                "Schreiber, I.M. & Mitch, W.A. — Nitrosamine Formation Pathway Revisited: The Importance of Chloramine Speciation and Dissolved Oxygen. Environ Sci Technol. 2006",
                "Krasner, S.W. et al. — Occurrence of a New Generation of Disinfection Byproducts. Environ Sci Technol. 2006",
                "Edwards, M. et al. — Corrosion of Lead by Chloramines in Drinking Water. J AWWA. 2004",
                "Hanna-Attisha, M. et al. — Elevated Blood Lead Levels in Children Associated With the Flint Drinking Water Crisis. Am J Public Health. 2016",
                "Jo, W.K. et al. — Chloroform Exposure and the Health Risk Associated with Multiple Uses of Chlorinated Tap Water. Risk Analysis. 1990",
                "Weisel, C.P. & Jo, W.K. — Ingestion, Inhalation, and Dermal Exposures to Chloroform and Trichloroethene from Tap Water. Environ Health Perspect. 1996",
                "Kogevinas, M. et al. — Total and Cancer Mortality in a Cohort of Swimmers. Occup Environ Med. 2010",
                "Toledano, M.B. et al. — Relation of Trihalomethane Concentrations in Public Water Supplies to Stillbirth and Birth Weight. Epidemiology. 2005",
                "IARC Monograph 73 — Chloroform (Group 2B) (1999)",
                "IARC Monograph 106 — Dichloroacetic Acid (Group 2A) (2014)",
                "Richardson, S.D. et al. — New Disinfection By-Product Issues: Emerging DBPs and Alternative Routes of Exposure. Global NEST J. 2010",
              ].map((s) => (
                <Text key={s} fontSize="xs" color="gray.500" lineHeight="tall">• {s}</Text>
              ))}
            </VStack>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Heading size="lg" color="white">Not sure what disinfectant is in your supply?</Heading>
              <Text color="whiteAlpha.800" maxW="md">
                Book a free water assessment. We'll identify whether your supply uses chlorine or chloramine, test your household levels, and recommend filtration that actually works for your situation.
              </Text>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={WATER_BLUE}
                  color={NAVY}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "#8DC5E0" }}
                  transition="all 0.2s"
                >
                  Book a Free Water Assessment
                </Button>
                <Button
                  as={RouterLink}
                  to="/water/water-filtration"
                  variant="outline"
                  borderColor={WATER_BLUE}
                  color={WATER_BLUE}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "whiteAlpha.100" }}
                >
                  Explore Filtration Options
                </Button>
              </HStack>
            </VStack>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogChlorine;
